import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { paperPlaneIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { BitwitInteraction } from '../../shared/interfaces/bitwit/bitwit-interaction';
import { BitwitPrompt, BitwitContextType } from '../../shared/interfaces/bitwit/bitwit-prompt';
import { BitwitAiService, BitwitAiConfig } from '../../shared/services/bitwit-ai.service';

interface ContextOption {
  label: string;
  value: BitwitContextType;
  description: string;
}

@Component({
    selector: 'app-bitwit',
    templateUrl: './bitwit.component.html',
    styleUrls: ['./bitwit.component.css'],
    standalone: false
})
export class BitwitComponent {
  readonly aiConfig: BitwitAiConfig;
  readonly paperPlaneIcon: SVGIcon = paperPlaneIcon;
  readonly contextOptions: ContextOption[] = [
    {
      label: 'RTA Intelligence',
      value: 'rta',
      description: 'Prioritize request-to-authorize process data.',
    },
    {
      label: 'Bit Intelligence',
      value: 'bit',
      description: 'Reason about dull condition, cutter maps, and offsets.',
    },
    {
      label: 'Hybrid',
      value: 'hybrid',
      description: 'Blend RTA and bit telemetry for holistic guidance.',
    },
  ];

  promptForm: FormGroup;

  interactions: BitwitInteraction[] = [];
  sending = false;
  lastError?: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly bitwitService: BitwitAiService,
  ) {
    this.aiConfig = this.bitwitService.config;
    this.promptForm = this.fb.group({
      prompt: ['', [Validators.required, Validators.minLength(4)]],
      contextType: ['hybrid' as BitwitContextType, Validators.required],
      rtaId: [''],
      bitSerial: [''],
      temperature: [
        this.aiConfig.defaultTemperature,
        [Validators.min(0), Validators.max(1)],
      ],
      maxTokens: [
        this.aiConfig.defaultMaxTokens,
        [Validators.min(64), Validators.max(4096)],
      ],
      includeTelemetry: [this.aiConfig.telemetryEnabled],
    });
  }

  get promptControl() {
    return this.promptForm.get('prompt');
  }

  sendPrompt(): void {
    if (this.promptForm.invalid) {
      this.promptForm.markAllAsTouched();
      return;
    }

    const prompt = this.buildPromptPayload();
    const userInteraction = this.createInteraction('user', prompt.prompt, prompt);
    this.interactions = [...this.interactions, userInteraction];

    const assistantInteraction = this.createInteraction(
      'assistant',
      'BitWit is synthesizing your context...',
      prompt,
      'pending',
    );
    const pendingIndex = this.interactions.length;
    this.interactions = [...this.interactions, assistantInteraction];

    this.sending = true;
    this.lastError = undefined;

    this.bitwitService
      .invoke(prompt)
      .pipe(finalize(() => (this.sending = false)))
      .subscribe({
        next: (response) => {
          this.interactions = this.interactions.map((interaction, index) => {
            if (index !== pendingIndex) {
              return interaction;
            }
            return {
              ...interaction,
              content: response.text,
              response,
              status: 'succeeded',
              timestamp: response.createdOn,
            };
          });
        },
        error: (error) => {
          const errorMessage = error?.message ?? 'Unknown BitWit error';
          this.lastError = errorMessage;
          this.interactions = this.interactions.map((interaction, index) => {
            if (index !== pendingIndex) {
              return interaction;
            }
            return {
              ...interaction,
              content: errorMessage,
              status: 'failed',
              error: errorMessage,
            };
          });
        },
      });
  }

  resetSession(): void {
    this.interactions = [];
    this.lastError = undefined;
  }

  reusePrompt(interaction: BitwitInteraction): void {
    if (!interaction.prompt) {
      return;
    }
    this.promptForm.patchValue({
      prompt: interaction.prompt.prompt,
      contextType: interaction.prompt.contextType,
      rtaId: interaction.prompt.rtaId || '',
      bitSerial: interaction.prompt.bitSerial || '',
      temperature: interaction.prompt.temperature,
      maxTokens: interaction.prompt.maxTokens,
      includeTelemetry: interaction.prompt.includeTelemetry,
    });
  }

  private buildPromptPayload(): BitwitPrompt {
    const value = this.promptForm.getRawValue();
    return {
      prompt: (value.prompt || '').trim(),
      contextType: value.contextType || 'hybrid',
      rtaId: value.rtaId?.trim() || undefined,
      bitSerial: value.bitSerial?.trim() || undefined,
      temperature: value.temperature ?? this.aiConfig.defaultTemperature,
      maxTokens: value.maxTokens ?? this.aiConfig.defaultMaxTokens,
      includeTelemetry: value.includeTelemetry ?? this.aiConfig.telemetryEnabled,
      metadata: {
        source: 'BitWitComponent',
      },
    };
  }

  private createInteraction(
    role: 'user' | 'assistant' | 'system',
    content: string,
    prompt?: BitwitPrompt,
    status: BitwitInteraction['status'] = 'succeeded',
  ): BitwitInteraction {
    return {
      id: `${role}-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      role,
      content,
      timestamp: new Date().toISOString(),
      prompt,
      status,
    };
  }
}
